import React from "react";
import { useParams, Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SYSTEMS, SYSTEM_BY_KEY, RESPONSIVITY_OPTIONS } from "@/lib/sensory";
import { TID } from "@/lib/testIds";
import { Save, ArrowLeft, BarChart3, Layers3, ClipboardList, Check, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Cell } from "recharts";
import { API_BASE } from "@/lib/api";
import { FEELINGS, FEELING_BY_KEY } from "@/lib/feelings";

export default function StudentDetail() {
  const { id } = useParams();
  const [student, setStudent] = React.useState(null);
  const [interventions, setInterventions] = React.useState([]);
  const [tab, setTab] = React.useState("profile");

  const load = async () => {
    const [{ data: s }, { data: iv }] = await Promise.all([
      api.get(`/students/${id}`),
      api.get("/interventions"),
    ]);
    setStudent(s);
    setInterventions(iv);
  };
  React.useEffect(() => { load(); }, [id]);

  if (!student) return <div>Loading…</div>;

  return (
    <div data-testid={TID.studentDetail} className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/therapist" className="sw-pill h-11"><ArrowLeft size={16} /> Back</Link>
      </div>
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Student</div>
          <h1 className="display mt-1 text-4xl md:text-5xl font-extrabold">{student.display_name}</h1>
          <div className="text-[var(--sw-ink-soft)] mt-1">@{student.username}</div>
        </div>
      </header>

      <nav className="flex gap-2 flex-wrap">
        <button data-testid={TID.tabProfile} onClick={() => setTab("profile")} className="sw-pill h-11" data-active={tab === "profile"}>
          <ClipboardList size={16} /> Sensory profile
        </button>
        <button data-testid={TID.tabBubbles} onClick={() => setTab("bubbles")} className="sw-pill h-11" data-active={tab === "bubbles"}>
          <Layers3 size={16} /> Curate bubbles
        </button>
        <button data-testid={TID.tabReport} onClick={() => setTab("report")} className="sw-pill h-11" data-active={tab === "report"}>
          <BarChart3 size={16} /> Selection report
        </button>
      </nav>

      {tab === "profile" && <ProfileEditor student={student} onSaved={load} />}
      {tab === "bubbles" && <BubbleCurator student={student} interventions={interventions} onSaved={load} />}
      {tab === "report" && <ReportView studentId={student.id} />}
    </div>
  );
}

function ProfileEditor({ student, onSaved }) {
  const [profile, setProfile] = React.useState(student.sensory_profile || {});
  const [busy, setBusy] = React.useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/students/${student.id}`, { sensory_profile: profile });
      toast.success("Profile saved.");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    setBusy(false);
  };
  return (
    <div className="sw-card p-6">
      <p className="text-sm text-[var(--sw-ink-soft)]">Mark responsivity for each system. This drives which bubbles are recommended in the curation step.</p>
      <div className="mt-5 grid md:grid-cols-2 gap-4">
        {SYSTEMS.map((sys) => {
          const Icon = sys.icon;
          return (
            <div key={sys.key} className="rounded-2xl border p-4" style={{ background: sys.bg, borderColor: sys.border }}>
              <div className="flex items-center gap-2"><Icon size={20} style={{ color: sys.color }} /><div className="display font-bold" style={{ color: sys.color }}>{sys.label}</div></div>
              <div className="text-xs mt-1" style={{ color: sys.color, opacity: 0.85 }}>{sys.meaning}</div>
              <select
                data-testid={TID.profileSelect(sys.key)}
                value={profile[sys.key] || "neutral"}
                onChange={(e) => setProfile((p) => ({ ...p, [sys.key]: e.target.value }))}
                className="mt-3 w-full h-11 px-3 rounded-xl border border-[var(--sw-line)] bg-white"
              >
                {RESPONSIVITY_OPTIONS.map((opt) => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      <button data-testid={TID.profileSave} onClick={save} disabled={busy} className="mt-5 sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]">
        <Save size={16} /> {busy ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}

function BubbleCurator({ student, interventions, onSaved }) {
  const [enabled, setEnabled] = React.useState(new Set(student.enabled_intervention_ids || []));
  const [busy, setBusy] = React.useState(false);
  const profile = student.sensory_profile || {};

  const toggle = (id) => {
    setEnabled((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/students/${student.id}`, { enabled_intervention_ids: Array.from(enabled) });
      toast.success(`${enabled.size} bubble${enabled.size === 1 ? "" : "s"} enabled.`);
      onSaved && onSaved();
    } catch (e) { toast.error("Could not save"); }
    setBusy(false);
  };

  const grouped = SYSTEMS.map((sys) => ({
    sys,
    items: interventions.filter((i) => i.system === sys.key),
  }));

  const isRecommended = (sys, iv) => {
    const resp = profile[sys] || "neutral";
    if (resp === "neutral") return true;
    return iv.responsivity_tag === resp || iv.responsivity_tag === "neutral";
  };

  return (
    <div className="space-y-5">
      <div className="sw-card p-4 flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-[var(--sw-ink-soft)]">
          Enable the bubbles this student should see. Bubbles tagged for their responsivity are highlighted as recommended.
        </div>
        <button data-testid={TID.bubblesSave} onClick={save} disabled={busy} className="sw-pill h-12 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]">
          <Save size={16} /> {busy ? "Saving…" : `Save (${enabled.size})`}
        </button>
      </div>

      {grouped.map(({ sys, items }) => {
        const Icon = sys.icon;
        return (
          <section key={sys.key} className="rounded-2xl border p-4" style={{ background: sys.bg, borderColor: sys.border }}>
            <div className="flex items-center gap-2 mb-3">
              <Icon size={22} style={{ color: sys.color }} />
              <h3 className="display font-bold text-xl" style={{ color: sys.color }}>{sys.boxLabel}</h3>
              <span className="text-xs uppercase font-semibold ml-auto" style={{ color: sys.color }}>
                Profile: {profile[sys.key] || "neutral"}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {items.map((iv) => {
                const on = enabled.has(iv.id);
                const recommended = isRecommended(sys.key, iv);
                return (
                  <button
                    type="button"
                    key={iv.id}
                    data-testid={TID.bubbleToggle(iv.id)}
                    onClick={() => toggle(iv.id)}
                    className={`text-left rounded-2xl p-4 border-2 transition bg-white ${on ? "" : ""}`}
                    style={{
                      borderColor: on ? sys.color : "rgba(0,0,0,0.08)",
                      boxShadow: on ? `0 8px 22px -14px ${sys.color}99` : undefined,
                    }}
                    aria-pressed={on}
                  >
                    <div className="flex items-start gap-2">
                      <div className="display font-bold text-base flex-1">{iv.bubble_name}</div>
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-white shrink-0`} style={{ background: on ? sys.color : "#D6D2C9" }}>
                        {on && <Check size={14} />}
                      </span>
                    </div>
                    <div className="text-xs mt-1 text-[var(--sw-ink-soft)] line-clamp-2">{iv.guidance_text}</div>
                    <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-wider font-semibold">
                      <span className={`px-2 py-0.5 rounded-full`} style={{ background: sys.bg, color: sys.color, border: `1px solid ${sys.border}` }}>{iv.responsivity_tag}</span>
                      {recommended && <span className="px-2 py-0.5 rounded-full bg-[var(--sw-amber)]/30 text-[var(--sw-primary-700)]">Recommended</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ReportView({ studentId }) {
  const [days, setDays] = React.useState(30);
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/students/${studentId}/report?days=${days}`)
      .then(({ data }) => setReport(data))
      .finally(() => setLoading(false));
  }, [studentId, days]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/students/${studentId}/report.pdf?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.href = url;
      a.download = m ? m[1] : `sensewise-report-${days}d.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded.");
    } catch (e) {
      toast.error("Could not download PDF");
    }
    setDownloading(false);
  };

  if (loading || !report) return <div className="text-[var(--sw-ink-soft)]">Loading report…</div>;

  const bySystemData = report.by_system.map((r) => {
    const sys = SYSTEM_BY_KEY[r.system] || {};
    return { name: sys.label || r.system, count: r.count, color: sys.color };
  });

  const feelingsList = report.by_feeling || [];
  const feelingRows = FEELINGS.map((f) => {
    const row = feelingsList.find((x) => x.feeling === f.key) || { count: 0 };
    return { key: f.key, name: f.prompt, count: row.count, color: f.color };
  });
  const noneRow = feelingsList.find((x) => x.feeling === "none") || { count: 0 };
  const feelingCount = feelingRows.reduce((n, r) => n + r.count, 0);
  const totalWithBox = feelingCount + noneRow.count;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} className="sw-pill h-11" data-active={days === d}>
            Last {d} days
          </button>
        ))}
        <button
          data-testid="report-download-pdf"
          onClick={downloadPdf}
          disabled={downloading}
          className="sw-pill h-11 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
        >
          <FileDown size={16} /> {downloading ? "Preparing…" : "Download PDF"}
        </button>
        <div className="ml-auto text-sm text-[var(--sw-ink-soft)]" data-testid={TID.reportTotal}>
          <strong className="text-[var(--sw-ink)] display text-xl">{report.total_selections}</strong> total selections
        </div>
      </div>

      <div data-testid={TID.reportChart} className="sw-card p-4 md:p-6">
        <h3 className="display text-xl font-bold mb-3">Selections by sensory system</h3>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bySystemData} margin={{ top: 16, right: 16, bottom: 24, left: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E6DFD3" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#3B4D44" }} interval={0} angle={-28} textAnchor="end" height={78} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#3B4D44" }} width={32} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E6DFD3", borderRadius: 12 }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]} fill="#2C4A3B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="sw-card p-4 md:p-6" data-testid="report-by-feeling">
        <div className="flex items-baseline justify-between gap-2 flex-wrap mb-3">
          <h3 className="display text-xl font-bold">Selections by feeling context</h3>
          <div className="text-xs text-[var(--sw-ink-soft)]">
            {feelingCount} of {totalWithBox || 0} started from a feeling &middot; {noneRow.count} started from a sensory box
          </div>
        </div>
        <p className="text-sm text-[var(--sw-ink-soft)] -mt-1 mb-4">
          Which feeling led {report.display_name?.split(" ")[0] || "this student"} to pick a strategy. Useful for IEP / MTSS notes.
        </p>
        {feelingCount === 0 && noneRow.count === 0 ? (
          <div className="text-sm text-[var(--sw-ink-soft)]">No selections in this window.</div>
        ) : (
          <>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={feelingRows} margin={{ top: 8, right: 16, bottom: 16, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 4" stroke="#E6DFD3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#3B4D44" }} height={40} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#3B4D44" }} width={32} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E6DFD3", borderRadius: 12 }} />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {feelingRows.map((row) => (
                      <Cell key={row.key} fill={row.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-2">
              {feelingRows.map((row) => {
                const f = FEELING_BY_KEY[row.key];
                return (
                  <li
                    key={row.key}
                    className="flex items-center gap-3 p-3 rounded-xl border"
                    style={{ background: f?.bg, borderColor: f?.border }}
                    data-testid={`feeling-row-${row.key}`}
                  >
                    <span className="display font-bold" style={{ color: row.color }}>{row.name}</span>
                    <span className="ml-auto display text-xl font-bold" style={{ color: row.color }}>{row.count}</span>
                  </li>
                );
              })}
              {noneRow.count > 0 && (
                <li className="flex items-center gap-3 p-3 rounded-xl border border-[var(--sw-line)] bg-[var(--sw-bg-soft)] sm:col-span-2" data-testid="feeling-row-none">
                  <span className="display font-bold text-[var(--sw-ink-soft)]">Started from a sensory box (no feeling recorded)</span>
                  <span className="ml-auto display text-xl font-bold text-[var(--sw-ink)]">{noneRow.count}</span>
                </li>
              )}
            </ul>
          </>
        )}
      </div>

      <div className="sw-card p-4 md:p-6">
        <h3 className="display text-xl font-bold mb-3">Selections by time of day</h3>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={report.by_hour}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E6DFD3" />
              <XAxis dataKey="hour" tickFormatter={(h) => `${h}:00`} tick={{ fontSize: 11, fill: "#3B4D44" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#3B4D44" }} />
              <Tooltip labelFormatter={(h) => `${h}:00`} contentStyle={{ background: "#fff", border: "1px solid #E6DFD3", borderRadius: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#D4A373" strokeWidth={3} dot={{ r: 4, fill: "#2C4A3B" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="sw-card p-4 md:p-6">
        <h3 className="display text-xl font-bold mb-3">Top bubbles</h3>
        {report.by_intervention.length === 0 ? (
          <div className="text-[var(--sw-ink-soft)] text-sm">No selections logged in this window.</div>
        ) : (
          <ul className="space-y-2">
            {report.by_intervention.slice(0, 10).map((b) => {
              const sys = SYSTEM_BY_KEY[b.system] || {};
              return (
                <li key={b.intervention_id} className="flex items-center gap-3 p-3 rounded-xl border" style={{ background: sys.bg, borderColor: sys.border }}>
                  <span className="display font-bold flex-1" style={{ color: sys.color }}>{b.bubble_name}</span>
                  <span className="text-xs uppercase font-semibold" style={{ color: sys.color }}>{sys.label}</span>
                  <span className="display text-xl font-bold" style={{ color: sys.color }}>{b.count}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
